/**
 * Theme System
 *
 * Pre-built and custom themes for landing pages.
 * Includes color schemes, typography, spacing, and layout settings.
 */

export interface PageTheme {
  id: string;
  name: string;
  description: string;
  category: 'preset' | 'custom';
  colors: {
    backgroundColor: string;
    accentColor: string;
    textColor: string;
    headingColor: string;
  };
  typography: {
    fontFamily: 'inter' | 'poppins' | 'merriweather' | 'system' | 'playfair' | 'roboto' | 'montserrat' | 'lora';
  };
  layout: {
    maxWidth: 'narrow' | 'medium' | 'wide';
    padding: 'compact' | 'normal' | 'spacious';
  };
  preview?: {
    gradient: string; // Tailwind gradient class
    icon: string; // Emoji or icon identifier
  };
}

// =============================================================================
// Pre-built Theme Presets
// =============================================================================

export const THEME_PRESETS: PageTheme[] = [
  {
    id: 'modern-light',
    name: 'Modern Light',
    description: 'Clean and minimalist with subtle colors',
    category: 'preset',
    colors: {
      backgroundColor: '#ffffff',
      accentColor: '#3b82f6',
      textColor: '#374151',
      headingColor: '#111827',
    },
    typography: {
      fontFamily: 'inter',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'normal',
    },
    preview: {
      gradient: 'from-blue-500 to-indigo-500',
      icon: '✨',
    },
  },
  {
    id: 'bold-gradient',
    name: 'Bold Gradient',
    description: 'High contrast with vibrant accents',
    category: 'preset',
    colors: {
      backgroundColor: '#ffffff',
      accentColor: '#ec4899',
      textColor: '#1f2937',
      headingColor: '#111827',
    },
    typography: {
      fontFamily: 'poppins',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'spacious',
    },
    preview: {
      gradient: 'from-pink-500 to-rose-500',
      icon: '🚀',
    },
  },
  {
    id: 'minimal-mono',
    name: 'Minimal Mono',
    description: 'Black and white simplicity',
    category: 'preset',
    colors: {
      backgroundColor: '#f9fafb',
      accentColor: '#111827',
      textColor: '#4b5563',
      headingColor: '#000000',
    },
    typography: {
      fontFamily: 'system',
    },
    layout: {
      maxWidth: 'narrow',
      padding: 'normal',
    },
    preview: {
      gradient: 'from-gray-800 to-gray-900',
      icon: '⚡',
    },
  },
  {
    id: 'nature-green',
    name: 'Nature Green',
    description: 'Earthy and calming greens',
    category: 'preset',
    colors: {
      backgroundColor: '#f0fdf4',
      accentColor: '#10b981',
      textColor: '#064e3b',
      headingColor: '#065f46',
    },
    typography: {
      fontFamily: 'merriweather',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'spacious',
    },
    preview: {
      gradient: 'from-emerald-500 to-teal-500',
      icon: '🌿',
    },
  },
  {
    id: 'sunset-warm',
    name: 'Sunset Warm',
    description: 'Warm oranges and yellows',
    category: 'preset',
    colors: {
      backgroundColor: '#fffbeb',
      accentColor: '#f59e0b',
      textColor: '#78350f',
      headingColor: '#92400e',
    },
    typography: {
      fontFamily: 'poppins',
    },
    layout: {
      maxWidth: 'wide',
      padding: 'normal',
    },
    preview: {
      gradient: 'from-amber-500 to-orange-500',
      icon: '🌅',
    },
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    description: 'Deep blues for tech and business',
    category: 'preset',
    colors: {
      backgroundColor: '#eff6ff',
      accentColor: '#2563eb',
      textColor: '#1e3a8a',
      headingColor: '#1e40af',
    },
    typography: {
      fontFamily: 'inter',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'normal',
    },
    preview: {
      gradient: 'from-blue-600 to-cyan-500',
      icon: '🌊',
    },
  },
  {
    id: 'purple-luxury',
    name: 'Purple Luxury',
    description: 'Premium feel with purple accents',
    category: 'preset',
    colors: {
      backgroundColor: '#faf5ff',
      accentColor: '#8b5cf6',
      textColor: '#581c87',
      headingColor: '#6b21a8',
    },
    typography: {
      fontFamily: 'poppins',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'spacious',
    },
    preview: {
      gradient: 'from-violet-500 to-purple-600',
      icon: '👑',
    },
  },
  {
    id: 'dark-mode',
    name: 'Dark Mode',
    description: 'Modern dark theme with neon accents',
    category: 'preset',
    colors: {
      backgroundColor: '#111827',
      accentColor: '#06b6d4',
      textColor: '#e5e7eb',
      headingColor: '#ffffff',
    },
    typography: {
      fontFamily: 'inter',
    },
    layout: {
      maxWidth: 'medium',
      padding: 'normal',
    },
    preview: {
      gradient: 'from-cyan-500 to-blue-500',
      icon: '🌙',
    },
  },
  {
    id: 'classic-serif',
    name: 'Classic Serif',
    description: 'Traditional and elegant',
    category: 'preset',
    colors: {
      backgroundColor: '#ffffff',
      accentColor: '#dc2626',
      textColor: '#374151',
      headingColor: '#1f2937',
    },
    typography: {
      fontFamily: 'merriweather',
    },
    layout: {
      maxWidth: 'narrow',
      padding: 'spacious',
    },
    preview: {
      gradient: 'from-red-600 to-rose-600',
      icon: '📖',
    },
  },
  {
    id: 'neon-cyber',
    name: 'Neon Cyber',
    description: 'Futuristic with bright neon colors',
    category: 'preset',
    colors: {
      backgroundColor: '#0f172a',
      accentColor: '#a855f7',
      textColor: '#cbd5e1',
      headingColor: '#f0abfc',
    },
    typography: {
      fontFamily: 'system',
    },
    layout: {
      maxWidth: 'wide',
      padding: 'compact',
    },
    preview: {
      gradient: 'from-purple-500 to-pink-500',
      icon: '🎮',
    },
  },
];

// =============================================================================
// Custom Theme Management
// =============================================================================

const CUSTOM_THEMES_KEY = 'arml_custom_themes';

/**
 * Get all saved custom themes from localStorage
 */
export function getCustomThemes(): PageTheme[] {
  try {
    const stored = localStorage.getItem(CUSTOM_THEMES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

/**
 * Save a custom theme to localStorage
 */
export function saveCustomTheme(theme: Omit<PageTheme, 'id' | 'category'>): PageTheme {
  const customTheme: PageTheme = {
    ...theme,
    id: `custom-${Date.now()}`,
    category: 'custom',
  };

  const existing = getCustomThemes();
  const updated = [...existing, customTheme];

  localStorage.setItem(CUSTOM_THEMES_KEY, JSON.stringify(updated));

  return customTheme;
}

/**
 * Delete a custom theme
 */
export function deleteCustomTheme(themeId: string): void {
  const existing = getCustomThemes();
  const filtered = existing.filter((t) => t.id !== themeId);
  localStorage.setItem(CUSTOM_THEMES_KEY, JSON.stringify(filtered));
}

/**
 * Get all themes (presets + custom)
 */
export function getAllThemes(): PageTheme[] {
  return [...THEME_PRESETS, ...getCustomThemes()];
}

/**
 * Get a specific theme by ID
 */
export function getThemeById(themeId: string): PageTheme | undefined {
  return getAllThemes().find((t) => t.id === themeId);
}

/**
 * Export theme as JSON for sharing
 */
export function exportTheme(theme: PageTheme): string {
  const exportData = {
    ...theme,
    id: undefined, // Remove ID for sharing
    category: 'custom' as const,
  };
  return JSON.stringify(exportData, null, 2);
}

/**
 * Import theme from JSON
 */
export function importTheme(jsonString: string): PageTheme | null {
  try {
    const parsed = JSON.parse(jsonString);

    // Validate required fields
    if (
      !parsed.name ||
      !parsed.colors ||
      !parsed.typography ||
      !parsed.layout
    ) {
      return null;
    }

    return saveCustomTheme({
      name: parsed.name,
      description: parsed.description || '',
      colors: parsed.colors,
      typography: parsed.typography,
      layout: parsed.layout,
      preview: parsed.preview,
    });
  } catch {
    return null;
  }
}
